/**
 * Brücke zwischen Warenkorb und Finanzierung.
 *
 * Geteilt von Frontend und Server. Der Browser schickt beim Absenden nur
 * Artikel-IDs und Mengen; welche Artikel finanzierbar sind, steht hier, die
 * Preise holt der Server aus Firestore (Spiegel von Lexoffice). Über den Request
 * lässt sich also kein Preis und kein Objekt unterschieben.
 *
 * Bewusst KEIN Import von `environment`: die Datei nutzt `$localize`, was beim
 * Route-Extraktor im Server-Bundle nicht existiert. Und die Objektnamen sollen
 * fest deutsch sein – ein Leasingvertrag darf nicht in der Sprache des
 * Websitebesuchers benannt sein.
 */

/** Versandkosten netto je versandpflichtiger Position – wie im ShopService. */
export const LEASING_VERSAND_NETTO: Record<string, number> = {
  DE: 379,
  AT: 479,
};

export type VersandLand = 'DE' | 'AT';
export type VersandArt = 'shipping' | 'collection';

export interface FinanzierbarerArtikel {
  /** Lexoffice-ID, identisch mit `IProductDTO.id` im Warenkorb. */
  readonly id: string;
  /** Objektbezeichnung für MLF und für die Anzeige – fest deutsch. */
  readonly bezeichnung: string;
  /** true = für jedes Stück fallen Versandkosten an (wie `shipping` im Shop). */
  readonly versandpflichtig: boolean;
}

/**
 * Ausdrückliche Freigabeliste statt „alles mit Tag Kaufen".
 *
 * Grund: im Shop-Katalog ist auch die **Mietkaution** mit „Kaufen" getaggt, und
 * eine Kaution ist kein Leasingobjekt. Umgekehrt ist die M2M-Simkarte, die
 * jedem Automaten beiliegt, mit „Mieten" getaggt und damit ohnehin außen vor.
 *
 * Wer hier einen Artikel ergänzt, muss die Lexoffice-ID aus
 * `environment.productsToFetch` übernehmen.
 */
export const FINANZIERBARE_ARTIKEL: readonly FinanzierbarerArtikel[] = [
  { id: '0c71c22a-fd84-4618-8e94-328dce545fb6', bezeichnung: 'marktbox 24', versandpflichtig: true },
  { id: '1ff8b1cd-49f9-4e5d-9231-0e39992140db', bezeichnung: 'marktbox 39', versandpflichtig: true },
  { id: 'f05c469d-00ea-4e85-9687-a565689b5c0c', bezeichnung: 'marktbox Extension 40', versandpflichtig: true },
  { id: '7ffeb228-3160-4c9a-9e3a-7a673575def9', bezeichnung: 'marktbox Extension 25', versandpflichtig: true },
  { id: '47b5e369-e27c-4f4c-8d37-527aab06e0af', bezeichnung: 'Zubehör: Hut', versandpflichtig: false },
  { id: 'a6e06b4d-da01-44ff-80c2-7b9335ae6651', bezeichnung: 'Zubehör: Ohren', versandpflichtig: false },
  { id: 'a01be5b2-ba4c-40b0-89d7-c7a6499e8939', bezeichnung: 'Zubehör: Befestigungswinkel', versandpflichtig: false },
  { id: 'c51f1cc8-08be-451a-811c-df1aca6a1bfd', bezeichnung: 'Zubehör: Alterskontrolle', versandpflichtig: false },
  // Liegt jedem Automaten zwangsweise bei. Finanziert wird der einmalige Kauf
  // der Karte (ein Monatsbetrag); das Abo danach läuft außerhalb der
  // Finanzierung weiter.
  { id: 'c3fae84d-2761-45bc-b685-5224184de701', bezeichnung: 'M2M-Simkarte', versandpflichtig: false },
];

/** Eine Position der Finanzierungsanfrage, Mengen bereits zusammengefasst. */
export interface LeasingPosition {
  artikelId: string;
  bezeichnung: string;
  anzahl: number;
  einzelpreisNetto: number;
}

/** Finanzierter Betrag einer Position: Stückpreis × Menge. */
export function positionsBetragNetto(position: LeasingPosition): number {
  return Math.round(position.anzahl * position.einzelpreisNetto * 100) / 100;
}

/** Lexoffice-ID der M2M-Simkarte – für den Hinweis in der Oberfläche. */
export const LEASING_SIMKARTE_ID = 'c3fae84d-2761-45bc-b685-5224184de701';

export function findeFinanzierbarenArtikel(id: string): FinanzierbarerArtikel | undefined {
  return FINANZIERBARE_ARTIKEL.find((a) => a.id === id);
}

export function istFinanzierbar(id: string): boolean {
  return FINANZIERBARE_ARTIKEL.some((a) => a.id === id);
}

/**
 * Versandkosten netto für eine Liste von Positionen. Bei Abholung null, sonst
 * Grundbetrag je versandpflichtigem Stück – ein Automat mit Erweiterung kostet
 * also zwei Mal Versand, genau wie im Warenkorb.
 */
export function leasingVersandNetto(
  positionen: readonly LeasingPosition[],
  land: VersandLand,
  art: VersandArt
): number {
  if (art === 'collection') {
    return 0;
  }
  const grundbetrag = LEASING_VERSAND_NETTO[land] ?? LEASING_VERSAND_NETTO['DE'];
  const stueck = positionen.reduce((summe, position) => {
    const artikel = findeFinanzierbarenArtikel(position.artikelId);
    return summe + (artikel?.versandpflichtig ? position.anzahl : 0);
  }, 0);
  return stueck * grundbetrag;
}

/** Objektwert netto = Warenwert plus Lieferung (die wird mitfinanziert). */
export function leasingObjektwertNetto(
  positionen: readonly LeasingPosition[],
  versandNetto: number
): number {
  const waren = positionen.reduce(
    (summe, position) => summe + positionsBetragNetto(position),
    0
  );
  return Math.round((waren + versandNetto) * 100) / 100;
}
